/**
 * TOSS Web Application — the collapsed rail's sub-menu
 *
 * On a 64 px rail a parent cannot expand — there is nowhere to expand INTO. Hovering it opens the
 * children beside the rail instead. Moved here from the internal `NavItem` (where it was
 * file-private) when the two sidebars merged: the portal's menu has parents now too.
 *
 * Rendered through a portal at `document.body`, because the rail is `sidebar-transition` —
 * `contain: layout style`, which makes it a containing block, and `overflow-y: auto` on the menu,
 * which would clip anything trying to leave it.
 *
 * The delays are the ones the internal rail was tuned to: 150 ms before opening, 100 ms before
 * closing, so crossing the gap between the row and the panel does not shut it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { SidebarNavNode } from './types';

// ============================================================================
// Types
// ============================================================================

export interface SidebarFlyoutProps {
  /** The row the panel hangs off. */
  children: ReactNode;
  label: string;
  items: SidebarNavNode[];
  /** Whether a child is the current screen — the surface's rule, see `types.ts`. */
  isItemActive: (item: SidebarNavNode) => boolean;
  onNavigate?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function SidebarFlyout({
  children,
  label,
  items,
  isItemActive,
  onNavigate,
}: SidebarFlyoutProps): JSX.Element {
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (): void => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovering(true);
    showTimeoutRef.current = setTimeout(() => setIsVisible(true), 150);
  };

  const handleMouseLeave = (): void => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setIsHovering(false);
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 100);
  };

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.top, left: rect.right + 8 });
    }
  }, [isVisible]);

  useEffect(
    () => () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    },
    []
  );

  return (
    <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}

      {isVisible &&
        createPortal(
          <div
            className={`app-surface app-surface-outlined fixed z-[9999] min-w-48 py-2 shadow-[var(--shadow-overlay)] transition-opacity duration-200 ease-out ${
              isHovering ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ top: position.top, left: position.left }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="px-3 pb-1.5 text-2xs font-medium uppercase tracking-widest text-[var(--color-text-secondary)]">
              {label}
            </div>

            <ul className="space-y-0.5 px-1">
              {items.map((item) => {
                const Icon = item.icon;
                const active = isItemActive(item);

                return (
                  <li key={item.id}>
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 h-10 px-3 rounded-[var(--radius-md)] text-sm transition-colors duration-150 ${
                        active
                          ? 'bg-[var(--color-primary-50)] text-[var(--color-primary-600)] font-medium'
                          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]'
                      }`}
                    >
                      {Icon && (
                        <Icon
                          className="w-[18px] h-[18px] flex-shrink-0"
                          strokeWidth={active ? 2.2 : 1.9}
                        />
                      )}
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
}

export default SidebarFlyout;
